import { creatorbase } from '@round/api';
import { SkeletonizedValue } from '@round/ui-kit';
import { FormField, SelectField, MoneyField, FileField, assets } from '@round/whitelabel-ui';
import { useFormikContext } from 'formik';
import { Banner } from '@round/whitelabel-ui';
import { ErrorMessage } from '@round/whitelabel-ui';
import omit from 'lodash/omit';
import { DebounceInput } from 'react-debounce-input';
import usePayeeSelect from 'Modules/Plans/Finance/Payee/hooks/usePayeeSelect';
import useAgencyPayeeSelect, { AgencyPayeeOption } from 'Modules/Plans/Finance/Payee/hooks/useAgencyPayeeSelect';
import AgencyPayeeBadge from 'Modules/Plans/Finance/Payee/components/AgencyPayeeBadge/AgencyPayeeBadge';
import { FormatOptionLabelMeta } from 'react-select';
import { useConditionalCurrencies } from 'contexts/OptionsContext/useConditionalCurrencies';
import ProviderOptions from '../ProviderOptions/ProviderOptions';
import { FormValues, Provider } from '../helpers';
import styles from './PaymentRequestForm.module.css';

const { TriangleExclamation: WarningIcon } = assets;

// Ordering hint floating an entity's account payee to the top of the dropdown. Post-only — a campaign
// has no single account, so it omits this and the dropdown uses the default order.
export type PayeeOrdering = Partial<
    Pick<
        creatorbase.GetClientPayeesParams,
        'order_by_tiktok_account_id' | 'order_by_instagram_account_id' | 'order_by_youtube_account_id'
    >
>;

type Props = {
    clientId: number | undefined;

    // Shows/hides the request-input region (provider/payee/paypal/agency/notes/file). When a request is
    // PENDING/PROCESSING the parent passes false and only a disabled cost field renders.
    canSubmitPaymentRequest: boolean;
    isLoading?: boolean;
    isDisabled?: boolean;
    isCostDisabled?: boolean;
    defaultProvider?: Provider | null;
    payeeOrdering?: PayeeOrdering;
    expectedPaypalEmail?: string;
    isInitialPayeeError?: boolean;
    hasFileError?: boolean;
};

const PaymentRequestForm = ({
    clientId,
    canSubmitPaymentRequest,
    isLoading,
    isDisabled,
    isCostDisabled,
    defaultProvider,
    payeeOrdering,
    expectedPaypalEmail,
    isInitialPayeeError,
    hasFileError,
}: Props) => {
    const { values, setFieldValue, initialValues, isSubmitting } = useFormikContext<FormValues>();
    const creatorbaseCurrencies = useConditionalCurrencies(true);

    const areFieldsDisabled = isDisabled || isSubmitting || !canSubmitPaymentRequest;
    const selectedProvider = values.provider || defaultProvider;

    const { getProps: getAgencyPayeeSelectProps } = useAgencyPayeeSelect({
        initialValue: initialValues?.agencyPayee,
    });

    const { getProps: getPayeeSelectProps } = usePayeeSelect({
        initialValue: initialValues?.payee,
        params: {
            client_id: clientId,
            is_payable: true,
            ...payeeOrdering,
        },
    });

    const isPaypalEmailDifferentToDefault = Boolean(
        expectedPaypalEmail && values.paypalEmail && values.paypalEmail !== expectedPaypalEmail
    );

    return (
        <>
            {canSubmitPaymentRequest && (
                <>
                    <SkeletonizedValue isInitialized={!isLoading} width="100%" height="4.375rem">
                        <div>
                            <ProviderOptions
                                selectedValue={values.provider || defaultProvider}
                                onChange={(provider) => setFieldValue('provider', provider)}
                                clientId={clientId}
                                isDisabled={areFieldsDisabled}
                            />
                            <ErrorMessage name="provider" />
                        </div>
                    </SkeletonizedValue>

                    {selectedProvider === 'TIPALTI' && (
                        <>
                            <SkeletonizedValue isInitialized={!isLoading} width="100%" height="3.875rem">
                                <SelectField
                                    name="payee"
                                    label="Payee"
                                    {...omit(getPayeeSelectProps(), ['value', 'onChange'])}
                                    placeholder="Select a payee"
                                />
                            </SkeletonizedValue>

                            {isInitialPayeeError && (
                                <Banner className={styles.banner} appearance="destructive">
                                    <WarningIcon className={styles.errorIcon} />
                                    <p>Something went wrong fetching selected payee</p>
                                </Banner>
                            )}
                        </>
                    )}

                    {selectedProvider === 'PAYPAL' && (
                        <>
                            <SkeletonizedValue isInitialized={!isLoading} width="100%" height="3.875rem">
                                <FormField
                                    name="paypalEmail"
                                    label="PayPal Email"
                                    placeholder="Enter paypal email"
                                    type="email"
                                    className={styles.paypalEmail}
                                    disabled={isSubmitting}
                                    component={(props: any) => {
                                        // props is any because there seems to be a discrepancy
                                        // between the actual props shape and type declaration.
                                        // In runtime props contains field props with classnames and etc and field config
                                        // component prop in type declaration is a field config (return type from useField)
                                        return <DebounceInput {...props} debounceTimeout={750} />;
                                    }}
                                />
                            </SkeletonizedValue>

                            {isPaypalEmailDifferentToDefault && (
                                <Banner className={styles.banner} appearance="warning">
                                    <WarningIcon className={styles.warningIcon} />
                                    <p>
                                        The PayPal email is not the same as the one on the influencer account:{' '}
                                        <strong>{expectedPaypalEmail}</strong>
                                    </p>
                                </Banner>
                            )}
                        </>
                    )}

                    {selectedProvider === 'CREATOR_AGENCY' && (
                        <SkeletonizedValue isInitialized={!isLoading} width="100%" height="3.875rem">
                            <SelectField
                                name="agencyPayee"
                                label="Agency"
                                {...omit(getAgencyPayeeSelectProps(), ['value', 'onChange'])}
                                placeholder="Select an agency"
                                formatOptionLabel={(
                                    option: AgencyPayeeOption,
                                    meta: FormatOptionLabelMeta<AgencyPayeeOption, false>
                                ) =>
                                    meta.context === 'menu' ? (
                                        <AgencyPayeeBadge name={option.label} imageUrl={option.image} />
                                    ) : (
                                        option.label
                                    )
                                }
                            />
                        </SkeletonizedValue>
                    )}

                    <SkeletonizedValue isInitialized={!isLoading} width="100%" height="4.375rem">
                        <FormField
                            component="textarea"
                            name="notes"
                            label="Notes"
                            placeholder="Enter payment notes"
                            className={styles.paymentNotes}
                            disabled={areFieldsDisabled}
                        />
                    </SkeletonizedValue>

                    <SkeletonizedValue isInitialized={!isLoading} width="100%" height="4.375rem">
                        <>
                            <FileField
                                name="fileUpload"
                                label="File Upload"
                                accept=".pdf,.png,.jpg,.jpeg,.txt,.csv,.doc,.docx"
                                disabled={areFieldsDisabled}
                                onRemove={() => setFieldValue('fileUpload', '')}
                            />
                            {hasFileError && (
                                <Banner className={styles.banner} appearance="destructive">
                                    <WarningIcon className={styles.errorIcon} />
                                    <p>Something went wrong fetching the file</p>
                                </Banner>
                            )}
                        </>
                    </SkeletonizedValue>
                </>
            )}

            <SkeletonizedValue isInitialized={!isLoading} width="100%" height="4.375rem">
                <MoneyField
                    name="cost"
                    currencyFieldName="currency"
                    label="Cost"
                    disabled={areFieldsDisabled || isCostDisabled}
                    isCurrencyDisabled={areFieldsDisabled || isCostDisabled}
                    className={styles.moneyInput}
                    currencies={creatorbaseCurrencies}
                />
            </SkeletonizedValue>
        </>
    );
};

export default PaymentRequestForm;
